import { Group, Stack, Text, Tooltip } from '@mantine/core'
import { faPlus } from '@fortawesome/sharp-duotone-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { OpenInMc1Link } from './OpenInMc1Link'
import { Button } from '../Common/Button'

export interface ClaimHeaderProps {
  title: string
  claimNumber: string
  insuredName: string
  insurerName: string
  dateOfLoss: string
  // Current status shown under the DOL. The editable copy stays in the Summary
  // tab's key data (MSD-538).
  presentPosition?: string
  // MC1 case id — makes the claim number a "View claim in MC1" link.
  caseId: number
  // Opens the File Note entry form (MSD-236).
  onAddFileNote: () => void
  // True once a note was saved from this email — only one per email is allowed.
  fileNoteDisabled?: boolean
  // Backend feature flag (EnableFileNote) — when off the button is not rendered at all.
  showFileNote?: boolean
}

export function ClaimHeader({
  title,
  claimNumber,
  insuredName,
  insurerName,
  dateOfLoss,
  presentPosition,
  caseId,
  onAddFileNote,
  fileNoteDisabled = false,
  showFileNote = true,
}: Readonly<ClaimHeaderProps>) {
  return (
    <Stack gap={4}>
      <Group justify="space-between" wrap="nowrap">
        <Stack gap={0} style={{ minWidth: 0 }}>
          <Tooltip label={title} withArrow openDelay={400} multiline maw={300}>
            <Text fw={600} fz="sm" truncate>
              {title}
            </Text>
          </Tooltip>
        </Stack>
        <OpenInMc1Link claimNumber={claimNumber} caseId={caseId} />
      </Group>
      <Group justify="space-between" wrap="nowrap">
        <Text fz="xs" truncate>
          Insurer: {insurerName}
        </Text>
        <Text fz="xs" style={{ whiteSpace: 'nowrap' }}>
          DOL: {dateOfLoss}
        </Text>
      </Group>
      <Group justify="space-between" align="center" wrap="nowrap">
        <Text fz="xs" truncate style={{ minWidth: 0 }}>
          Insured: {insuredName}
        </Text>
        {presentPosition && (
          <Tooltip label={presentPosition} withArrow openDelay={400} multiline maw={300}>
            <Text fz="xs" truncate style={{ minWidth: 0 }}>
              {presentPosition}
            </Text>
          </Tooltip>
        )}
      </Group>
      {/* The whole row goes, not just the Button — an empty Group would still take up
          the Stack's gap and leave a hole under the header. */}
      {showFileNote && (
        <Group justify="flex-end" align="center" wrap="nowrap">
          <Tooltip
            label={fileNoteDisabled ? 'A file note was already added from this email' : 'Add a file note to this claim'}
            withArrow
          >
            {/* data-disabled instead of disabled so the explanatory tooltip still fires. */}
            <Button
              label="Add File Note"
              size="compact-xs"
              variant="light"
              data-disabled={fileNoteDisabled || undefined}
              aria-disabled={fileNoteDisabled}
              rightSection={<FontAwesomeIcon icon={faPlus} size="xs" />}
              onClick={() => {
                if (!fileNoteDisabled) onAddFileNote()
              }}
            />
          </Tooltip>
        </Group>
      )}
    </Stack>
  )
}
